// "List an item" form for the Listings tab — bottom strip of the tab
// body. Extracted from market-listings-tab.ts to respect the §10
// 400-line cap and to keep the table-render and form-render concerns
// independently readable.
//
// State (selected slot, current price, pending flag) is owned by
// MarketUI and flows in via the snapshot param on every build().

import { Container, Graphics, Text } from "pixi.js";
import { balance } from "../../shared/balance.ts";
import type { ItemId } from "../../shared/items.ts";
import { getItemDef, items } from "../../shared/items.ts";
import type { InventorySlot } from "../../shared/messages.ts";
import { parseItemId, QUALITY_COLORS, QUALITY_LABELS } from "../../shared/quality.ts";
import {
  BTN_H,
  BUTTON_PALETTE_DEFAULT,
  makeRowButton,
  renderSpinBtn,
  SPIN_BTN_SZ,
} from "./market-ui-widgets.ts";

const PAD = 12; // contracts:allow-literal — inner padding px
// Phase 7 Block D — taller form to fit the fee row + non-refundable
// caption. The fee row sits below the price spinner and List button.
const FORM_H = 124; // contracts:allow-literal — form height px
const SWATCH_SZ = 18; // contracts:allow-literal — item swatch side
const LIST_BTN_W = 90; // contracts:allow-literal — List button width

// Phase 7 Block D — listing-fee copy (audit finding 9.4 was a UI/copy
// gap, not a design bug). Exported so browser tests can assert the
// rendered text against the same string constants.
export const LIST_FORM_FEE_NOTICE = "Listing fees are non-refundable.";
export const LIST_FORM_FEE_LABEL = "Fee";

// Mirrors server-side `feeFor(price)` in market-listings.ts / zone-market.ts:
// ceil(price * listingFeeRatio) with a 1c floor.
export function computeListingFee(price: number): number {
  return Math.max(1, Math.ceil(price * balance.economy.listingFeeRatio));
}

const TABLE_BG = 0x0e0e22;
const TABLE_BORDER = 0x223366;
const TEXT_BRIGHT = 0xeeeeff;
const TEXT_MUTED = 0x8888aa;
const TEXT_PLACEHOLDER = 0x556688;
const TEXT_FEE_NOTICE = 0xddaa66;
const BORDER_DEFAULT = 0x555555;
const BODY_SIZE = 11; // contracts:allow-literal
const FEE_NOTICE_SIZE = 10; // contracts:allow-literal — small caption
const LABEL_SIZE = 12; // contracts:allow-literal

export type ListFormSnapshot = {
  selectedSlot: InventorySlot | null;
  listPrice: number;
  listPending: boolean;
  priceMin: number;
  priceMax: number;
};

export type ListFormCallbacks = {
  onList: (itemId: ItemId, price: number) => void;
  onPriceDelta: (delta: number) => void;
};

export { FORM_H };

export function buildListForm(
  parent: Container,
  x: number,
  y: number,
  w: number,
  snap: ListFormSnapshot,
  cb: ListFormCallbacks,
): void {
  const form = new Container();
  form.x = x;
  form.y = y;
  parent.addChild(form);

  form.addChild(
    new Graphics().rect(0, 0, w, FORM_H).fill({ color: TABLE_BG }).stroke({ color: TABLE_BORDER, width: 1 }), // contracts:allow-literal
  );

  const title = new Text({
    text: "List an item",
    style: { fontSize: LABEL_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace", fontWeight: "bold" },
  });
  title.x = PAD;
  title.y = PAD / 2; // contracts:allow-literal — half-pad for title
  form.addChild(title);

  const rowY = title.y + title.height + 6; // contracts:allow-literal — gap under title
  let cx = PAD;

  const selected = snap.selectedSlot;
  if (selected) {
    const itemDef = getItemDef(selected.itemId) ?? items.copper;
    const { quality } = parseItemId(selected.itemId);
    const swatchBorder = quality ? QUALITY_COLORS[quality] : BORDER_DEFAULT;
    const swatchBorderW = quality ? 2 : 1; // contracts:allow-literal — quality vs default stroke
    const swatch = new Graphics()
      .roundRect(0, 0, SWATCH_SZ, SWATCH_SZ, 3) // contracts:allow-literal — corner radius
      .fill({ color: itemDef.color })
      .stroke({ color: swatchBorder, width: swatchBorderW });
    swatch.x = cx;
    swatch.y = rowY;
    form.addChild(swatch);
    cx += SWATCH_SZ + 6; // contracts:allow-literal — gap

    const nameLabel = quality ? `${QUALITY_LABELS[quality]} ${itemDef.name}` : itemDef.name;
    const nameText = new Text({
      text: nameLabel,
      style: { fontSize: BODY_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace" },
    });
    nameText.x = cx;
    nameText.y = rowY + Math.round((SWATCH_SZ - nameText.height) / 2); // contracts:allow-literal
    form.addChild(nameText);
    cx += nameText.width + 16; // contracts:allow-literal — gap to spinner
  } else {
    const hint = new Text({
      text: "Click an inventory slot to select…",
      style: { fontSize: BODY_SIZE, fill: TEXT_PLACEHOLDER, fontFamily: "monospace", fontStyle: "italic" },
    });
    hint.x = cx;
    hint.y = rowY + Math.round((SWATCH_SZ - hint.height) / 2); // contracts:allow-literal
    form.addChild(hint);
    cx += hint.width + 16; // contracts:allow-literal — gap
  }

  const spinnerEnabled = selected !== null && !snap.listPending;
  cx = buildPriceSpinner(form, cx, rowY, snap, spinnerEnabled, cb);

  const listBtnX = w - PAD - LIST_BTN_W;
  const canList =
    selected !== null &&
    !snap.listPending &&
    snap.listPrice >= snap.priceMin &&
    snap.listPrice <= snap.priceMax;
  const listBtn = makeRowButton({
    label: snap.listPending ? "Listing…" : "List",
    palette: BUTTON_PALETTE_DEFAULT,
    disabled: !canList,
    width: LIST_BTN_W,
    onClick: () => {
      if (!selected) return;
      cb.onList(selected.itemId, snap.listPrice);
    },
  });
  listBtn.x = listBtnX;
  listBtn.y = rowY + Math.round((SWATCH_SZ - BTN_H) / 2); // contracts:allow-literal
  form.addChild(listBtn);

  // Phase 7 Block D — fee row + non-refundable caption. Sits below the
  // price/list row so the player sees what the listing will cost before
  // they hit List. Caption renders on its own line.
  const fee = computeListingFee(snap.listPrice);
  const feeRowY = rowY + SWATCH_SZ + 8; // contracts:allow-literal — gap under price/list row
  const feeText = new Text({
    text: `${LIST_FORM_FEE_LABEL}: ₵ ${fee}`,
    style: { fontSize: BODY_SIZE, fill: TEXT_MUTED, fontFamily: "monospace" },
  });
  feeText.x = PAD;
  feeText.y = feeRowY;
  form.addChild(feeText);

  const noticeText = new Text({
    text: LIST_FORM_FEE_NOTICE,
    style: { fontSize: FEE_NOTICE_SIZE, fill: TEXT_FEE_NOTICE, fontFamily: "monospace", fontStyle: "italic" },
  });
  noticeText.x = PAD + feeText.width + 12; // contracts:allow-literal — gap after fee value
  noticeText.y = feeRowY + Math.round((feeText.height - noticeText.height) / 2); // contracts:allow-literal
  form.addChild(noticeText);
}

function buildPriceSpinner(
  parent: Container,
  x: number,
  rowY: number,
  snap: ListFormSnapshot,
  enabled: boolean,
  cb: ListFormCallbacks,
): number {
  let cx = x;
  const btnY = rowY + Math.round((SWATCH_SZ - SPIN_BTN_SZ) / 2); // contracts:allow-literal
  const lbl = new Text({
    text: "Price:",
    style: { fontSize: BODY_SIZE, fill: TEXT_MUTED, fontFamily: "monospace" },
  });
  lbl.x = cx;
  lbl.y = rowY + Math.round((SWATCH_SZ - lbl.height) / 2); // contracts:allow-literal
  parent.addChild(lbl);
  cx += lbl.width + 4; // contracts:allow-literal — gap

  cx = renderSpinBtn(parent, cx, btnY, "−", enabled && snap.listPrice > snap.priceMin, (step) => cb.onPriceDelta(-step)) + 4; // contracts:allow-literal — gap

  const priceLbl = new Text({
    text: `\u20B5 ${snap.listPrice}`,
    style: { fontSize: BODY_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace", fontWeight: "bold" },
  });
  const priceDispW = 64; // contracts:allow-literal — fixed width so spinner doesn't jump
  priceLbl.x = cx + Math.round((priceDispW - priceLbl.width) / 2); // contracts:allow-literal — centering idiom
  priceLbl.y = rowY + Math.round((SWATCH_SZ - priceLbl.height) / 2); // contracts:allow-literal
  parent.addChild(priceLbl);
  cx += priceDispW + 4; // contracts:allow-literal — gap

  renderSpinBtn(parent, cx, btnY, "+", enabled && snap.listPrice < snap.priceMax, (step) => cb.onPriceDelta(step));
  cx += SPIN_BTN_SZ;
  return cx;
}
